/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { jsx, css } from '@atlaskit/css';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { type JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { fontFallback } from '@atlaskit/theme/typography';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import type { EditorAgent } from '../../../utils/agents';
import { AI_MODAL_SCREEN_CLASS } from '../../../utils/constants';
import EllipsesAnimation from '../../assets/Ellipses';
import LoadingIcon from '../../assets/Loading';
import RovoIcon from '../../assets/RovoIcon';
import { CancelEscButton } from '../../components/CancelEscButton/CancelEscButton';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { Footer } from '../../components/Footer/Footer';
import { type Disclaimer } from '../../components/Footer/types';
import { LatestPromptHeader } from '../../components/LatestPromptHeader/LatestPromptHeader';

import messages from './messages';

const defaultPaddingStyles = css({
	paddingTop: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),
	paddingRight: token('space.150'),
});

const horizontalPaddingStyles = css({
	paddingLeft: token('space.200'),
	paddingRight: token('space.200'),
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const previewContainer = cssUnbounded({
	borderTop: `1px solid ${token('color.border')}`,
	borderBottom: `1px solid ${token('color.border')}`,
});

const layoutStyles = css({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'left',
	alignItems: 'center',
	flexWrap: 'wrap',
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const logoStyles = cssUnbounded({
	marginRight: token('space.025'),
	display: 'flex',
	alignItems: 'center',
});

const newLogoStyles = cssUnbounded({ gap: '8px' });

const titleLayoutStyles = css({ flex: 1 });
const commonTextStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body', fontFallback.body.medium),
	wordBreak: 'break-word',
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const titleStyles = [titleLayoutStyles, commonTextStyles];

const loadingIconStyles = css({ flex: 1 });

/**
 * Could be either:
 * - userInput only (free form generate / interrogate)
 * - presetTitle only (summarize)
 * - both presetTitle and userInput (brainstorm)
 */
type LatestPrompt =
	| { presetTitle?: never; userInput: string; userADFInput?: JSONDocNode }
	| { presetTitle: string; userInput?: string; userADFInput?: JSONDocNode };

type Props = {
	/**
	 * Label for the parent if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
	 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
	 */
	parentPresetPromptLabel?: string;
	/**
	 * Short title for the submenu if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
	 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
	 */
	childShortTitle?: string;
	/**
	 * Label for the selected partial prompt.
	 */
	presetPromptLabel?: string;
	title: string;
	latestPrompt?: LatestPrompt;
	/**
	 * Pass undefined when there is no previewable content
	 */
	preview: React.ReactElement | undefined;
	providerFactory: ProviderFactory;
	schema: Schema;
	onCancel: () => void;
	agent?: EditorAgent;
	disclaimer?: Disclaimer;
	additionalFooterText?: string;
	isStreaming?: boolean;
};

export const LoadingScreen = ({
	parentPresetPromptLabel,
	childShortTitle,
	presetPromptLabel,
	title,
	latestPrompt,
	preview,
	providerFactory,
	schema,
	onCancel,
	agent,
	disclaimer,
	additionalFooterText,
	isStreaming,
}: Props) => {
	const { formatMessage } = useIntl();

	return (
		<FloatingContainer
			data-testid="loading-screen"
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={`loading-screen ${AI_MODAL_SCREEN_CLASS}`}
			rainbowBorderAnimation={editorExperiment('platform_editor_ai_aifc', true) ? false : true}
			rainbowBorder={editorExperiment('platform_editor_ai_aifc', true) ? false : true}
			disablePadding
			enableMaxWidth={editorExperiment('platform_editor_ai_aifc', true)}
		>
			{editorExperiment('platform_editor_ai_aifc', true) ? (
				<div css={[defaultPaddingStyles, layoutStyles, horizontalPaddingStyles]}>
					<div data-testid="rovo-icon" css={[newLogoStyles, logoStyles]}>
						<RovoIcon label={'RovoIcon'} />
						<div css={titleStyles}>
							{isStreaming
								? formatMessage(messages.loadingEditingTitle)
								: formatMessage(messages.loadingAriaNewTitle)}
						</div>
					</div>
					<div css={loadingIconStyles}>
						<EllipsesAnimation />
					</div>
					<CancelEscButton autoFocus onCancel={onCancel} shape="circle" />
				</div>
			) : (
				<div>
					<VisuallyHidden>{formatMessage(messages.loadingAriaTitle)}</VisuallyHidden>
					{/**
					 * we will be passing userADFInput here,
					 *  when LatestPromptHeader starts using Renderer.
					 * Will be done in https://product-fabric.atlassian.net/browse/EDF-367
					 */}
					<LatestPromptHeader
						parentPresetPromptLabel={parentPresetPromptLabel}
						childShortTitle={childShortTitle}
						prompt={latestPrompt?.userInput}
						promptADF={latestPrompt?.userADFInput}
						presetTitle={latestPrompt?.presetTitle}
						providerFactory={providerFactory}
						schema={schema}
						agent={agent}
						showBack={true}
					/>
					{preview && (
						/*
						 * We use a live region so that content updates will be announced to screenreaders. We set:
						 * - aria-atomic="false": because if aria-atomic were set to true, the screen reader would keep
						 * reannouncing the content from the beginning rather than providing updates only for the newly added content.
						 * - aria-live="polite": to ensure that updates are announced by screen readers in a non-disruptive manner,
						 * allowing the user to finish their current task before the new content is communicated.
						 *
						 * We also add a visually hidden description for the live region ("Live updating content:") so that
						 * there is at least some minimal context presented to the user about the incoming updates.
						 */
						<div
							data-testid="adf-content-live-region"
							aria-atomic="false"
							aria-live="polite"
							css={[previewContainer, defaultPaddingStyles]}
							/* eslint-enable */
						>
							<VisuallyHidden testId="description">
								{formatMessage(messages.liveRegionDescription)}
							</VisuallyHidden>
							{preview}
						</div>
					)}
					<div css={[defaultPaddingStyles, layoutStyles]}>
						<div data-testid="loading-icon" css={logoStyles}>
							<LoadingIcon alt={formatMessage(messages.iconAltText)} />
						</div>
						<div css={titleStyles}>{title}</div>
						<CancelEscButton autoFocus onCancel={onCancel} />
					</div>
					<Footer
						brand={agent ? 'rovo' : 'atlassian-intelligence'}
						disclaimer={disclaimer}
						showDisclaimer={true}
					/>
				</div>
			)}
		</FloatingContainer>
	);
};
